import React, { useState, useMemo } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, LineChart, Line, ScatterChart, Scatter,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { Calculator, TrendingUp, BarChart3, Table, Award, FileText } from 'lucide-react';
import './DescriptiveStatistics.css';

const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6'];

const DescriptiveStatistics = ({ survey }) => {
  const [activeCategory, setActiveCategory] = useState('central');
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [crossTabQ1, setCrossTabQ1] = useState(null);
  const [crossTabQ2, setCrossTabQ2] = useState(null);
  const [vizType, setVizType] = useState('bar');
  const [vizQuestion, setVizQuestion] = useState(null);

  const responses = survey?.responses || [];
  const questions = survey?.questions || [];

  const numericQuestions = useMemo(() => 
    questions.filter(q => ['linearScale', 'likertScale', 'number'].includes(q.type)),
    [questions]
  );

  const categoricalQuestions = useMemo(() => 
    questions.filter(q => ['multipleChoice', 'dropdown', 'yesNo', 'checkboxes'].includes(q.type)),
    [questions]
  );

  const getNumericAnswers = (questionId) => {
    return responses
      .map(r => r.answers?.[questionId])
      .filter(a => a !== undefined && a !== null && a !== '')
      .map(a => typeof a === 'number' ? a : parseFloat(a))
      .filter(n => !isNaN(n));
  };

  const getCategoricalAnswers = (question) => {
    const answers = responses
      .map(r => r.answers?.[question.id])
      .filter(a => a !== undefined && a !== null && a !== '');
    
    if (question.type === 'checkboxes') {
      return answers.flat();
    }
    return answers;
  };

  const calculateMean = (values) => {
    if (values.length === 0) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  const calculateMedian = (values) => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  const calculateMode = (values) => {
    if (values.length === 0) return [];
    const frequency = {};
    values.forEach(v => {
      frequency[v] = (frequency[v] || 0) + 1;
    });
    const maxFreq = Math.max(...Object.values(frequency));
    return Object.keys(frequency).filter(k => frequency[k] === maxFreq).map(Number);
  };

  const calculateTrimmedMean = (values, trimPercent = 0.1) => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const trimCount = Math.floor(sorted.length * trimPercent);
    const trimmed = sorted.slice(trimCount, sorted.length - trimCount);
    return calculateMean(trimmed);
  };

  const calculateVariance = (values) => {
    if (values.length === 0) return 0;
    const mean = calculateMean(values);
    return values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
  };

  const calculateStdDev = (values) => {
    return Math.sqrt(calculateVariance(values));
  };

  const calculateRange = (values) => {
    if (values.length === 0) return { min: 0, max: 0, range: 0 };
    const min = Math.min(...values);
    const max = Math.max(...values);
    return { min, max, range: max - min };
  };

  const calculateIQR = (values) => {
    if (values.length === 0) return { q1: 0, q3: 0, iqr: 0 };
    const sorted = [...values].sort((a, b) => a - b);
    const q1Idx = Math.floor(sorted.length * 0.25);
    const q3Idx = Math.floor(sorted.length * 0.75);
    const q1 = sorted[q1Idx];
    const q3 = sorted[q3Idx];
    return { q1, q3, iqr: q3 - q1 };
  };

  const calculateCV = (values) => {
    const mean = calculateMean(values);
    const stdDev = calculateStdDev(values);
    return mean !== 0 ? (stdDev / mean) * 100 : 0;
  };

  const calculateSkewness = (values) => {
    if (values.length < 3) return 0;
    const n = values.length;
    const mean = calculateMean(values);
    const stdDev = calculateStdDev(values);
    if (stdDev === 0) return 0;
    const sum = values.reduce((acc, v) => acc + Math.pow((v - mean) / stdDev, 3), 0);
    return (n / ((n - 1) * (n - 2))) * sum;
  };

  const calculateKurtosis = (values) => {
    if (values.length < 4) return 0;
    const n = values.length;
    const mean = calculateMean(values);
    const stdDev = calculateStdDev(values);
    if (stdDev === 0) return 0;
    const sum = values.reduce((acc, v) => acc + Math.pow((v - mean) / stdDev, 4), 0);
    return ((n * (n + 1)) / ((n - 1) * (n - 2) * (n - 3))) * sum - (3 * Math.pow(n - 1, 2)) / ((n - 2) * (n - 3));
  };

  const calculatePercentile = (values, percentile) => {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const idx = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[Math.max(0, idx)];
  };

  const calculateZScores = (values) => {
    const mean = calculateMean(values);
    const stdDev = calculateStdDev(values);
    if (stdDev === 0) return values.map(() => 0);
    return values.map(v => (v - mean) / stdDev);
  };

  const getFrequencyTable = (values) => {
    const frequency = {};
    values.forEach(v => {
      frequency[v] = (frequency[v] || 0) + 1;
    });
    const total = values.length;
    let cumulative = 0;
    return Object.entries(frequency)
      .sort((a, b) => a[0] - b[0])
      .map(([value, count]) => {
        cumulative += count;
        return {
          value,
          frequency: count,
          relativeFreq: ((count / total) * 100).toFixed(1),
          cumulativeFreq: cumulative,
          cumulativePercent: ((cumulative / total) * 100).toFixed(1)
        };
      });
  };

  const getCategoricalFrequency = (question) => {
    const options = question.options || question.settings?.options || [];
    const frequency = {};
    const isCheckbox = question.type === 'checkboxes';
    
    if (question.type === 'yesNo') {
      frequency['yes'] = 0;
      frequency['no'] = 0;
    } else {
      options.forEach(opt => {
        frequency[opt.id] = 0;
      });
    }
    
    let respondentsWhoAnswered = 0;
    let totalSelections = 0;
    
    if (isCheckbox) {
      responses.forEach(r => {
        const answer = r.answers?.[question.id];
        if (Array.isArray(answer) && answer.length > 0) {
          respondentsWhoAnswered++;
          answer.forEach(a => {
            if (frequency[a] !== undefined) {
              frequency[a]++;
              totalSelections++;
            }
          });
        }
      });
    } else {
      const answers = getCategoricalAnswers(question);
      respondentsWhoAnswered = answers.length;
      answers.forEach(a => {
        if (frequency[a] !== undefined) {
          frequency[a]++;
        }
      });
    }

    const totalAnswers = isCheckbox ? respondentsWhoAnswered : Object.values(frequency).reduce((sum, c) => sum + c, 0);
    let cumulative = 0;

    const getLabel = (id) => {
      if (question.type === 'yesNo') return id === 'yes' ? 'Yes' : 'No';
      const opt = options.find(o => o.id === id);
      return opt?.text || id;
    };

    return Object.entries(frequency).map(([id, count]) => {
      if (!isCheckbox) {
        cumulative += count;
      }
      return {
        id,
        label: getLabel(id),
        frequency: count,
        relativeFreq: totalAnswers > 0 ? ((count / totalAnswers) * 100).toFixed(1) : '0.0',
        respondentPercent: isCheckbox && respondentsWhoAnswered > 0 ? ((count / respondentsWhoAnswered) * 100).toFixed(1) : null,
        selectionPercent: isCheckbox && totalSelections > 0 ? ((count / totalSelections) * 100).toFixed(1) : null,
        cumulativeFreq: isCheckbox ? null : cumulative,
        cumulativePercent: isCheckbox ? null : (totalAnswers > 0 ? ((cumulative / totalAnswers) * 100).toFixed(1) : '0.0'),
        isCheckbox,
        totalRespondents: respondentsWhoAnswered,
        totalSelections: isCheckbox ? totalSelections : null
      };
    });
  };

  const calculateCrosstab = (q1, q2) => {
    if (!q1 || !q2) return null;
    
    const getOptions = (q) => {
      if (q.type === 'yesNo') return [{ id: 'yes', text: 'Yes' }, { id: 'no', text: 'No' }];
      return q.options || q.settings?.options || [];
    };

    const opts1 = getOptions(q1);
    const opts2 = getOptions(q2);
    
    const table = {};
    opts1.forEach(o1 => {
      table[o1.id] = {};
      opts2.forEach(o2 => {
        table[o1.id][o2.id] = 0;
      });
    });

    responses.forEach(r => {
      const a1 = r.answers?.[q1.id];
      const a2 = r.answers?.[q2.id];
      if (a1 && a2 && table[a1] && table[a1][a2] !== undefined) {
        table[a1][a2]++;
      }
    });

    return { table, opts1, opts2 };
  };

  const getFiveNumberSummary = (values) => {
    if (values.length === 0) return { min: 0, q1: 0, median: 0, q3: 0, max: 0 };
    const sorted = [...values].sort((a, b) => a - b);
    return {
      min: sorted[0],
      q1: calculatePercentile(values, 25),
      median: calculateMedian(values),
      q3: calculatePercentile(values, 75),
      max: sorted[sorted.length - 1]
    };
  };

  const getResponseRate = () => {
    const totalPossible = responses.length * questions.length;
    let answered = 0;
    responses.forEach(r => {
      questions.forEach(q => {
        const answer = r.answers?.[q.id];
        if (answer !== undefined && answer !== null && answer !== '') {
          answered++;
        }
      });
    });
    return totalPossible > 0 ? ((answered / totalPossible) * 100).toFixed(1) : '0.0';
  };

  const getCompletionRate = () => {
    if (responses.length === 0) return '0.0';
    const complete = responses.filter(r => {
      return questions.every(q => {
        const answer = r.answers?.[q.id];
        return answer !== undefined && answer !== null && answer !== '';
      });
    }).length;
    return ((complete / responses.length) * 100).toFixed(1);
  };

  const getMissingDataAnalysis = () => {
    return questions.map(q => {
      const answered = responses.filter(r => {
        const answer = r.answers?.[q.id];
        return answer !== undefined && answer !== null && answer !== '';
      }).length;
      const missing = responses.length - answered;
      return {
        question: q.text,
        answered,
        missing,
        missingPercent: responses.length > 0 ? ((missing / responses.length) * 100).toFixed(1) : '0.0'
      };
    });
  };

  const categories = [
    { id: 'central', label: 'Central Tendency', icon: Calculator },
    { id: 'variability', label: 'Variability/Dispersion', icon: TrendingUp },
    { id: 'distribution', label: 'Distribution Shape', icon: BarChart3 },
    { id: 'frequency', label: 'Frequency Distributions', icon: Table },
    { id: 'visualizations', label: 'Visualizations', icon: BarChart3 },
    { id: 'crosstab', label: 'Crosstabulations', icon: Table },
    { id: 'position', label: 'Position & Rankings', icon: Award },
    { id: 'summaries', label: 'Data Summaries', icon: FileText }
  ];

  const renderCentralTendency = () => {
    if (numericQuestions.length === 0) {
      return <p className="no-data">No numeric questions available for central tendency analysis.</p>;
    }

    return (
      <div className="stat-section">
        <h3>Central Tendency Measures</h3>
        <table className="stat-table">
          <thead>
            <tr>
              <th>Question</th>
              <th>Mean</th>
              <th>Median</th>
              <th>Mode</th>
              <th>Trimmed Mean (10%)</th>
            </tr>
          </thead>
          <tbody>
            {numericQuestions.map(q => {
              const values = getNumericAnswers(q.id);
              return (
                <tr key={q.id}>
                  <td className="question-cell">{q.text}</td>
                  <td>{calculateMean(values).toFixed(2)}</td>
                  <td>{calculateMedian(values).toFixed(2)}</td>
                  <td>{calculateMode(values).join(', ') || 'N/A'}</td>
                  <td>{calculateTrimmedMean(values).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderVariability = () => {
    if (numericQuestions.length === 0) {
      return <p className="no-data">No numeric questions available for variability analysis.</p>;
    }

    return (
      <div className="stat-section">
        <h3>Variability/Dispersion Measures</h3>
        <table className="stat-table">
          <thead>
            <tr>
              <th>Question</th>
              <th>Std Dev</th>
              <th>Variance</th>
              <th>Range</th>
              <th>IQR</th>
              <th>CV (%)</th>
            </tr>
          </thead>
          <tbody>
            {numericQuestions.map(q => {
              const values = getNumericAnswers(q.id);
              const range = calculateRange(values);
              const iqr = calculateIQR(values);
              return (
                <tr key={q.id}>
                  <td className="question-cell">{q.text}</td>
                  <td>{calculateStdDev(values).toFixed(2)}</td>
                  <td>{calculateVariance(values).toFixed(2)}</td>
                  <td>{range.min} - {range.max} ({range.range})</td>
                  <td>{iqr.iqr.toFixed(2)}</td>
                  <td>{calculateCV(values).toFixed(2)}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderDistributionShape = () => {
    if (numericQuestions.length === 0) {
      return <p className="no-data">No numeric questions available for distribution analysis.</p>;
    }

    return (
      <div className="stat-section">
        <h3>Distribution Shape</h3>
        <table className="stat-table">
          <thead>
            <tr>
              <th>Question</th>
              <th>Skewness</th>
              <th>Kurtosis</th>
              <th>P25</th>
              <th>P50 (Median)</th>
              <th>P75</th>
            </tr>
          </thead>
          <tbody>
            {numericQuestions.map(q => {
              const values = getNumericAnswers(q.id);
              return (
                <tr key={q.id}>
                  <td className="question-cell">{q.text}</td>
                  <td>{calculateSkewness(values).toFixed(3)}</td>
                  <td>{calculateKurtosis(values).toFixed(3)}</td>
                  <td>{calculatePercentile(values, 25).toFixed(2)}</td>
                  <td>{calculatePercentile(values, 50).toFixed(2)}</td>
                  <td>{calculatePercentile(values, 75).toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="interpretation-box">
          <h4>Interpretation Guide</h4>
          <p><strong>Skewness:</strong> 0 = symmetric, &gt;0 = right-skewed (tail to right), &lt;0 = left-skewed</p>
          <p><strong>Kurtosis:</strong> 0 = normal, &gt;0 = heavy tails (leptokurtic), &lt;0 = light tails (platykurtic)</p>
        </div>
      </div>
    );
  };

  const renderFrequencyDistributions = () => {
    const allQuestions = [...numericQuestions, ...categoricalQuestions];
    
    if (allQuestions.length === 0) {
      return <p className="no-data">No questions available for frequency analysis.</p>;
    }

    const currentQuestion = selectedQuestion || allQuestions[0];
    const isNumeric = numericQuestions.some(q => q.id === currentQuestion.id);

    return (
      <div className="stat-section">
        <h3>Frequency Distributions</h3>
        <div className="question-selector">
          <label>Select Question:</label>
          <select 
            value={currentQuestion?.id || ''} 
            onChange={(e) => setSelectedQuestion(allQuestions.find(q => q.id === e.target.value))}
          >
            {allQuestions.map(q => (
              <option key={q.id} value={q.id}>{q.text}</option>
            ))}
          </select>
        </div>

        {isNumeric ? (
          <table className="stat-table">
            <thead>
              <tr>
                <th>Value</th>
                <th>Frequency</th>
                <th>Relative Freq (%)</th>
                <th>Cumulative Freq</th>
                <th>Cumulative %</th>
              </tr>
            </thead>
            <tbody>
              {getFrequencyTable(getNumericAnswers(currentQuestion.id)).map((row, idx) => (
                <tr key={idx}>
                  <td>{row.value}</td>
                  <td>{row.frequency}</td>
                  <td>{row.relativeFreq}%</td>
                  <td>{row.cumulativeFreq}</td>
                  <td>{row.cumulativePercent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : currentQuestion.type === 'checkboxes' ? (
          <>
            {(() => {
              const freqData = getCategoricalFrequency(currentQuestion);
              const stats = freqData[0] || {};
              return (
                <>
                  <p className="checkbox-note">
                    Note: For checkbox questions, respondents can select multiple options. 
                    Showing {stats.totalRespondents || 0} respondents who answered with {stats.totalSelections || 0} total selections.
                  </p>
                  <table className="stat-table">
                    <thead>
                      <tr>
                        <th>Option</th>
                        <th>Count</th>
                        <th>% of Respondents</th>
                        <th>% of Selections</th>
                      </tr>
                    </thead>
                    <tbody>
                      {freqData.map((row, idx) => (
                        <tr key={idx}>
                          <td>{row.label}</td>
                          <td>{row.frequency}</td>
                          <td>{row.respondentPercent !== null ? `${row.respondentPercent}%` : 'N/A'}</td>
                          <td>{row.selectionPercent !== null ? `${row.selectionPercent}%` : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              );
            })()}
          </>
        ) : (
          <table className="stat-table">
            <thead>
              <tr>
                <th>Option</th>
                <th>Frequency</th>
                <th>Relative Freq (%)</th>
                <th>Cumulative Freq</th>
                <th>Cumulative %</th>
              </tr>
            </thead>
            <tbody>
              {getCategoricalFrequency(currentQuestion).map((row, idx) => (
                <tr key={idx}>
                  <td>{row.label}</td>
                  <td>{row.frequency}</td>
                  <td>{row.relativeFreq}%</td>
                  <td>{row.cumulativeFreq}</td>
                  <td>{row.cumulativePercent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderVisualizations = () => {
    const allQuestions = [...categoricalQuestions, ...numericQuestions];
    if (allQuestions.length === 0) {
      return <p className="no-data">No questions available for visualization.</p>;
    }

    const currentQ = vizQuestion || allQuestions[0];
    const isNumeric = numericQuestions.some(q => q.id === currentQ.id);

    const getChartData = () => {
      if (isNumeric) {
        const freq = getFrequencyTable(getNumericAnswers(currentQ.id));
        return freq.map(row => ({ name: row.value, value: row.frequency }));
      } else {
        const freq = getCategoricalFrequency(currentQ);
        return freq.map(row => ({ name: row.label, value: row.frequency }));
      }
    };

    const chartData = getChartData();

    const getSpiderData = () => {
      if (categoricalQuestions.length === 0) return [];
      return categoricalQuestions.slice(0, 6).map(q => {
        const freq = getCategoricalFrequency(q);
        const maxFreq = Math.max(...freq.map(f => f.frequency));
        return {
          question: q.text.slice(0, 20) + (q.text.length > 20 ? '...' : ''),
          value: maxFreq,
          fullMark: responses.length
        };
      });
    };

    const getStackedBarData = () => {
      if (!isNumeric) {
        const freq = getCategoricalFrequency(currentQ);
        const dataPoint = { name: 'Responses' };
        freq.forEach(row => {
          dataPoint[row.label] = row.frequency;
        });
        return { data: [dataPoint], keys: freq.map(row => row.label) };
      } else {
        const freq = getFrequencyTable(getNumericAnswers(currentQ.id));
        const dataPoint = { name: 'Responses' };
        freq.forEach(row => {
          dataPoint[String(row.value)] = row.frequency;
        });
        return { data: [dataPoint], keys: freq.map(row => String(row.value)) };
      }
    };

    const stackedData = getStackedBarData();
    const stackColors = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4'];

    return (
      <div className="stat-section">
        <h3>Visualizations</h3>
        <div className="viz-controls">
          <div className="question-selector">
            <label>Question:</label>
            <select value={currentQ?.id || ''} onChange={(e) => setVizQuestion(allQuestions.find(q => q.id === e.target.value))}>
              {allQuestions.map(q => (
                <option key={q.id} value={q.id}>{q.text}</option>
              ))}
            </select>
          </div>
          <div className="viz-type-selector">
            <label>Chart Type:</label>
            <select value={vizType} onChange={(e) => setVizType(e.target.value)}>
              <option value="bar">Bar Chart</option>
              <option value="stackedBar">Stacked Bar Chart</option>
              <option value="pie">Pie Chart</option>
              <option value="histogram">Histogram</option>
              <option value="line">Line Graph</option>
              <option value="scatter">Scatter Plot</option>
              <option value="spider">Spider Diagram</option>
            </select>
          </div>
        </div>

        <div className="chart-container">
          {vizType === 'bar' && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  interval={0}
                  height={80}
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  label={{ value: 'Frequency', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <Tooltip />
                <Bar dataKey="value" fill="#6366f1" name="Count" />
              </BarChart>
            </ResponsiveContainer>
          )}

          {vizType === 'stackedBar' && (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={stackedData.data} layout="vertical" margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number"
                  label={{ value: 'Frequency', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle' } }}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={80}
                />
                <Tooltip />
                <Legend />
                {stackedData.keys.map((key, index) => (
                  <Bar 
                    key={key} 
                    dataKey={key} 
                    stackId="stack" 
                    fill={stackColors[index % stackColors.length]}
                    name={key}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}

          {vizType === 'pie' && (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}

          {vizType === 'histogram' && isNumeric && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  label={{ value: 'Value', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle' } }}
                />
                <YAxis 
                  label={{ value: 'Frequency', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <Tooltip />
                <Bar dataKey="value" fill="#8b5cf6" name="Frequency" />
              </BarChart>
            </ResponsiveContainer>
          )}

          {vizType === 'line' && (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                  height={80}
                  tick={{ fontSize: 11 }}
                />
                <YAxis 
                  label={{ value: 'Frequency', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} name="Count" />
              </LineChart>
            </ResponsiveContainer>
          )}

          {vizType === 'scatter' && isNumeric && numericQuestions.length >= 2 && (
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="x" 
                  name={numericQuestions[0]?.text?.slice(0, 30) || 'X'}
                  label={{ value: numericQuestions[0]?.text?.slice(0, 40) || 'X Variable', position: 'insideBottom', offset: -5, style: { textAnchor: 'middle', fontSize: 11 } }}
                />
                <YAxis 
                  dataKey="y" 
                  name={numericQuestions[1]?.text?.slice(0, 30) || 'Y'}
                  label={{ value: numericQuestions[1]?.text?.slice(0, 40) || 'Y Variable', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 11 } }}
                />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                <Scatter 
                  name="Data" 
                  data={responses.slice(0, 100).map((r, i) => ({
                    x: r.answers?.[numericQuestions[0]?.id] || 0,
                    y: r.answers?.[numericQuestions[1]?.id] || 0
                  }))} 
                  fill="#6366f1" 
                />
              </ScatterChart>
            </ResponsiveContainer>
          )}

          {vizType === 'spider' && (
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={getSpiderData()}>
                <PolarGrid />
                <PolarAngleAxis dataKey="question" />
                <PolarRadiusAxis />
                <Radar name="Responses" dataKey="value" stroke="#6366f1" fill="#6366f1" fillOpacity={0.6} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  };

  const renderCrosstabulations = () => {
    const crosstabQuestions = categoricalQuestions.filter(q => q.type !== 'checkboxes');
    
    if (crosstabQuestions.length < 2) {
      return <p className="no-data">Need at least 2 single-choice categorical questions for crosstabulation. Checkbox questions are excluded as they allow multiple selections.</p>;
    }

    const q1 = crossTabQ1 || crosstabQuestions[0];
    const q2 = crossTabQ2 || crosstabQuestions[1];
    const crosstab = calculateCrosstab(q1, q2);

    if (!crosstab) return null;

    const { table, opts1, opts2 } = crosstab;
    const rowTotals = {};
    const colTotals = {};
    let grandTotal = 0;

    opts1.forEach(o1 => {
      rowTotals[o1.id] = 0;
      opts2.forEach(o2 => {
        const count = table[o1.id][o2.id];
        rowTotals[o1.id] += count;
        colTotals[o2.id] = (colTotals[o2.id] || 0) + count;
        grandTotal += count;
      });
    });

    return (
      <div className="stat-section">
        <h3>Crosstabulations (Contingency Table)</h3>
        <div className="crosstab-selectors">
          <div className="question-selector">
            <label>Row Variable:</label>
            <select value={q1?.id || ''} onChange={(e) => setCrossTabQ1(crosstabQuestions.find(q => q.id === e.target.value))}>
              {crosstabQuestions.map(q => (
                <option key={q.id} value={q.id}>{q.text}</option>
              ))}
            </select>
          </div>
          <div className="question-selector">
            <label>Column Variable:</label>
            <select value={q2?.id || ''} onChange={(e) => setCrossTabQ2(crosstabQuestions.find(q => q.id === e.target.value))}>
              {crosstabQuestions.map(q => (
                <option key={q.id} value={q.id}>{q.text}</option>
              ))}
            </select>
          </div>
        </div>

        <table className="stat-table crosstab-table">
          <thead>
            <tr>
              <th></th>
              {opts2.map(o2 => <th key={o2.id}>{o2.text}</th>)}
              <th>Row Total</th>
            </tr>
          </thead>
          <tbody>
            {opts1.map(o1 => (
              <tr key={o1.id}>
                <td className="row-header">{o1.text}</td>
                {opts2.map(o2 => (
                  <td key={o2.id}>
                    {table[o1.id][o2.id]}
                    <span className="cell-percent">
                      ({grandTotal > 0 ? ((table[o1.id][o2.id] / grandTotal) * 100).toFixed(1) : 0}%)
                    </span>
                  </td>
                ))}
                <td className="total-cell">{rowTotals[o1.id]}</td>
              </tr>
            ))}
            <tr className="total-row">
              <td className="row-header">Column Total</td>
              {opts2.map(o2 => <td key={o2.id} className="total-cell">{colTotals[o2.id] || 0}</td>)}
              <td className="grand-total">{grandTotal}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderPositionRankings = () => {
    if (numericQuestions.length === 0) {
      return <p className="no-data">No numeric questions available for position analysis.</p>;
    }

    const currentQ = selectedQuestion || numericQuestions[0];
    const values = getNumericAnswers(currentQ.id);
    const zScores = calculateZScores(values);

    const getDeciles = () => {
      return [10, 20, 30, 40, 50, 60, 70, 80, 90].map(p => ({
        decile: `D${p/10}`,
        value: calculatePercentile(values, p).toFixed(2)
      }));
    };

    const getQuintiles = () => {
      return [20, 40, 60, 80].map(p => ({
        quintile: `Q${p/20}`,
        value: calculatePercentile(values, p).toFixed(2)
      }));
    };

    return (
      <div className="stat-section">
        <h3>Position & Rankings</h3>
        <div className="question-selector">
          <label>Select Question:</label>
          <select value={currentQ?.id || ''} onChange={(e) => setSelectedQuestion(numericQuestions.find(q => q.id === e.target.value))}>
            {numericQuestions.map(q => (
              <option key={q.id} value={q.id}>{q.text}</option>
            ))}
          </select>
        </div>

        <div className="position-grid">
          <div className="position-card">
            <h4>Deciles</h4>
            <table className="mini-table">
              <tbody>
                {getDeciles().map(d => (
                  <tr key={d.decile}>
                    <td>{d.decile}</td>
                    <td>{d.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="position-card">
            <h4>Quintiles</h4>
            <table className="mini-table">
              <tbody>
                {getQuintiles().map(q => (
                  <tr key={q.quintile}>
                    <td>{q.quintile}</td>
                    <td>{q.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="position-card">
            <h4>Z-Scores (Sample)</h4>
            <table className="mini-table">
              <tbody>
                {values.slice(0, 10).map((v, i) => (
                  <tr key={i}>
                    <td>Value: {v}</td>
                    <td>Z: {zScores[i]?.toFixed(3) || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderDataSummaries = () => {
    return (
      <div className="stat-section">
        <h3>Data Summaries</h3>

        <div className="summary-cards">
          <div className="summary-card">
            <h4>Overall Statistics</h4>
            <div className="stat-row">
              <span>Total Responses:</span>
              <strong>{responses.length}</strong>
            </div>
            <div className="stat-row">
              <span>Response Rate:</span>
              <strong>{getResponseRate()}%</strong>
            </div>
            <div className="stat-row">
              <span>Completion Rate:</span>
              <strong>{getCompletionRate()}%</strong>
            </div>
          </div>
        </div>

        {numericQuestions.length > 0 && (
          <>
            <h4>Five-Number Summary</h4>
            <table className="stat-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Min</th>
                  <th>Q1</th>
                  <th>Median</th>
                  <th>Q3</th>
                  <th>Max</th>
                </tr>
              </thead>
              <tbody>
                {numericQuestions.map(q => {
                  const summary = getFiveNumberSummary(getNumericAnswers(q.id));
                  return (
                    <tr key={q.id}>
                      <td className="question-cell">{q.text}</td>
                      <td>{summary.min}</td>
                      <td>{summary.q1.toFixed(2)}</td>
                      <td>{summary.median.toFixed(2)}</td>
                      <td>{summary.q3.toFixed(2)}</td>
                      <td>{summary.max}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}

        <h4>Missing Data Analysis</h4>
        <table className="stat-table">
          <thead>
            <tr>
              <th>Question</th>
              <th>Answered</th>
              <th>Missing</th>
              <th>Missing %</th>
            </tr>
          </thead>
          <tbody>
            {getMissingDataAnalysis().map((row, idx) => (
              <tr key={idx}>
                <td className="question-cell">{row.question}</td>
                <td>{row.answered}</td>
                <td>{row.missing}</td>
                <td>{row.missingPercent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeCategory) {
      case 'central':
        return renderCentralTendency();
      case 'variability':
        return renderVariability();
      case 'distribution':
        return renderDistributionShape();
      case 'frequency':
        return renderFrequencyDistributions();
      case 'visualizations':
        return renderVisualizations();
      case 'crosstab':
        return renderCrosstabulations();
      case 'position':
        return renderPositionRankings();
      case 'summaries':
        return renderDataSummaries();
      default:
        return null;
    }
  };

  if (responses.length === 0) {
    return (
      <div className="descriptive-stats empty">
        <p>No responses available. Generate sample responses or collect real responses to view descriptive statistics.</p>
      </div>
    );
  }

  return (
    <div className="descriptive-stats">
      <div className="stats-sidebar">
        <h3>Categories</h3>
        {categories.map(cat => {
          const Icon = cat.icon;
          return (
            <button
              key={cat.id}
              className={`category-btn ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <Icon size={18} />
              {cat.label}
            </button>
          );
        })}
      </div>
      <div className="stats-content">
        {renderContent()}
      </div>
    </div>
  );
};

export default DescriptiveStatistics;
